import React from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import useFeatureFlagStore from '../store/featureFlagStore';
import Sidebar from '../components/shared/Sidebar';
import ErrorAlert from '../components/shared/ErrorAlert';
import ThemeToggle from '../components/shared/ThemeToggle';
import DynamicDashboardRenderer from '../components/dashboard/DynamicDashboardRenderer';
import { UserService } from '../services/userService';
import { User } from '../types/user';
import organisationSettingsService from '../services/organisationSettingsService';
// Logo replaced with futuristic text

const DashboardPage: React.FC = () => {
  const { user, logout } = useAuthStore();
  const { isFeatureEnabled, error, isLoading } = useFeatureFlagStore();
  const navigate = useNavigate();
  const [showError, setShowError] = React.useState(!!error);
  
  // Admin user selection state
  const [selectedUserId, setSelectedUserId] = React.useState<number | null>(null);
  const [allUsers, setAllUsers] = React.useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = React.useState(false);
  const [selectedUserName, setSelectedUserName] = React.useState<string>('');
  
  // Whitelabel settings state
  const [productName, setProductName] = React.useState<string>('Lumina AI');
  const [whitelabelLogo, setWhitelabelLogo] = React.useState<string | null>(null);

  // Clear error message
  const handleClearError = () => {
    setShowError(false);
  };

  // Update showError when error changes
  React.useEffect(() => {
    setShowError(!!error);
  }, [error]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // If user is not available, redirect to login
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Fetch users for admin/group admin dropdown
  React.useEffect(() => {
    const fetchUsers = async () => {
      if (user?.role === 'admin' || user?.role === 'group_admin') {
        try {
          setLoadingUsers(true);
          const users = await UserService.getAllUsers();
          setAllUsers(users);
        } catch (error) {
          console.error('Failed to fetch users:', error);
        } finally {
          setLoadingUsers(false);
        }
      }
    };

    fetchUsers();
  }, [user?.role]);

  // Load whitelabel settings
  React.useEffect(() => {
    const loadWhitelabelSettings = async () => {
      if (!user) return;
      
      try {
        const settings = await organisationSettingsService.getOrganisationSettings();
        
        if (settings.whitelabelProductName) {
          setProductName(settings.whitelabelProductName);
        } else {
          setProductName('Lumina AI');
        }
        
        if (settings.whitelabelPrimaryLogoUrl) {
          const baseUrl = settings.whitelabelPrimaryLogoUrl.startsWith('http') 
            ? settings.whitelabelPrimaryLogoUrl 
            : `${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '')}${settings.whitelabelPrimaryLogoUrl}`;
          setWhitelabelLogo(`${baseUrl}?t=${Date.now()}`);
        } else {
          setWhitelabelLogo(null);
        }
      } catch (error) {
        console.error('Error loading whitelabel settings:', error);
      }
    };

    loadWhitelabelSettings();
    
    // Listen for whitelabel settings updates
    const handleWhitelabelUpdate = () => {
      loadWhitelabelSettings();
    };
    
    window.addEventListener('whitelabelSettingsUpdated', handleWhitelabelUpdate);
    
    return () => {
      window.removeEventListener('whitelabelSettingsUpdated', handleWhitelabelUpdate);
    };
  }, [user]);

  // Handle user selection change
  const handleUserSelectionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    if (value === '') {
      setSelectedUserId(null);
      setSelectedUserName('');
    } else {
      const userId = parseInt(value);
      setSelectedUserId(userId);
      const selectedUser = allUsers.find(u => parseInt(u.id) === userId);
      setSelectedUserName(selectedUser?.name || '');
    }
  };

  // Safely access user data with null checks
  const userInitial = user?.name ? user.name.charAt(0) : '';
  const userName = user?.name || 'User';

  return (
    <div className="dashboard-page">
      <Sidebar />
      
      {/* Display error alert if there's an error */}
      {showError && <ErrorAlert message={error} onClose={handleClearError} />}
      
      <div className="dashboard-main">
        <header className="dashboard-header">
          <div className="dashboard-header-content">
            <img 
              src={whitelabelLogo || "/LuminaAilogoPNG.png"} 
              alt={productName} 
              className="dashboard-logo-image"
            />
            <p className="dashboard-tagline">{productName === 'Lumina AI' ? 'Your data, uncovered' : productName}</p>
          </div>
          <div className="user-info">
            <div className="themed-user-badge">
              <div className="user-avatar">
                {userInitial}
              </div>
              <div className="user-details">
                <span className="welcome-text">Welcome, {userName}</span>
                {user?.role === 'admin' && (
                  <span className="user-role">Admin</span>
                )}
                {user?.role === 'group_admin' && (
                  <span className="user-role">Group Admin</span>
                )}
              </div>
            </div>
            <ThemeToggle />
            <button onClick={handleLogout} className="logout-button">Logout</button>
          </div>
        </header>
        
        <div className="dashboard-content">
          {/* Admin/Group Admin User Selection Dropdown */}
          {(user?.role === 'admin' || user?.role === 'group_admin') && (
            <div className="admin-user-selector" style={{ 
              marginBottom: '24px', 
              padding: '16px', 
              background: 'linear-gradient(135deg, rgba(0, 30, 60, 0.8) 0%, rgba(0, 40, 80, 0.6) 100%)',
              borderRadius: '12px',
              border: '2px solid rgba(0, 200, 255, 0.3)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 0 20px rgba(0, 200, 255, 0.3), 0 0 40px rgba(0, 200, 255, 0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <label htmlFor="user-select" style={{ 
                  fontWeight: '600', 
                  color: 'rgba(0, 200, 255, 0.9)',
                  minWidth: 'fit-content',
                  textShadow: '0 0 8px rgba(0, 200, 255, 0.3)'
                }}>
                  👤 View Statistics For:
                </label>
                {user?.role === 'group_admin' && (
                  <span style={{ 
                    fontSize: '12px', 
                    color: 'rgba(255, 255, 255, 0.6)', 
                    fontStyle: 'italic',
                    marginLeft: '8px'
                  }}>
                    (Your managed users only)
                  </span>
                )}
                <select
                  id="user-select"
                  value={selectedUserId?.toString() || ''}
                  onChange={handleUserSelectionChange}
                  disabled={loadingUsers}
                  className="dark-select-dropdown"
                  style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '2px solid rgba(0, 200, 255, 0.3)',
                    background: '#1a2332',
                    backgroundColor: '#1a2332',
                    color: '#ffffff',
                    fontSize: '14px',
                    minWidth: '200px',
                    cursor: loadingUsers ? 'not-allowed' : 'pointer',
                    boxShadow: '0 0 10px rgba(0, 200, 255, 0.2), inset 0 0 10px rgba(0, 200, 255, 0.05)'
                  }}
                >
                  <option value="" style={{ background: '#1a2332', color: '#ffffff' }}>
                    {loadingUsers ? 'Loading users...' : `My Statistics (${userName})`}
                  </option>
                  {allUsers.length === 0 && !loadingUsers && user?.role === 'group_admin' && (
                    <option disabled style={{ background: '#1a2332', color: '#888888' }}>No managed users yet - create some users first</option>
                  )}
                  {allUsers.map(u => (
                    <option key={u.id} value={u.id} style={{ background: '#1a2332', color: '#ffffff' }}>
                      {u.name} ({u.email}) {u.role === 'admin' ? '👑' : u.role === 'group_admin' ? '👥' : ''}
                    </option>
                  ))}
                </select>
                {selectedUserName && (
                  <span style={{ 
                    color: 'rgba(255, 255, 255, 0.7)', 
                    fontSize: '14px',
                    fontStyle: 'italic'
                  }}>
                    Currently viewing: <strong>{selectedUserName}</strong>
                  </span>
                )}
              </div>
            </div>
          )}
          
          <DynamicDashboardRenderer selectedUserId={selectedUserId || undefined} />
        </div>
        
        <footer className="dashboard-footer">
          <div className="footer-content">
                            <p>&copy; 2025 ASE Global - Accelerating Financial Validation</p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default DashboardPage; 